import { type ICallAccount } from '@/types/ICallAccount';
import { isRecord } from './isRecord';

/**
 * Type guard аккаунта из payload звонка. Сверяет только ключ учётки — `uuid`
 * **или** `id`, — ровно то, по чему работают ссылка на аккаунт и фильтр
 * заказов клиента.
 *
 * До PAR-5189 требовался числовой `id`. С бэкенда `par-5087` его у учёток нет
 * ни в одной админской ручке, и строгая проверка отбрасывала бы не аккаунт, а
 * **всё событие звонка** (`isCallEventData`): ни тоста входящего, ни модалки.
 *
 * Телефон здесь намеренно НЕ требуется: аккаунт без номера — штатный сценарий
 * (авторизация через сторонние сервисы), а номер звонящего мы в любом случае
 * берём из `data.incoming_phone`. Требовать `phone` значило бы терять звонок
 * целиком из-за незаполненного второстепенного поля.
 *
 * @param value - произвольное значение (поле `data.user` события)
 * @returns `true`, если значение соответствует форме `ICallAccount`
 */
export const isCallAccount = (value: unknown): value is ICallAccount =>
  isRecord(value) &&
  ((typeof value.uuid === 'string' && value.uuid !== '') ||
    typeof value.id === 'number');

/**
 * Ключ учётки звонящего для фильтра заказов `client_id`: `uuid`, а если его
 * нет — `id` прежнего бэкенда. v2 принимает в `client_id` оба.
 *
 * @param account - учётка звонящего либо `null`
 * @returns ключ либо `null`, если опознать учётку нечем
 */
export const getCallAccountKey = (
  account: ICallAccount | null
): string | number | null => {
  if (account === null) return null;
  if (typeof account.uuid === 'string' && account.uuid !== '') {
    return account.uuid;
  }
  return account.id ?? null;
};
